// slice.ts
//
// (c) NCR Voyix Corporation 2025. All rights reserved.

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Site } from '../../data/sites'
import { BreadcrumbSegment } from '../../components/BreadcrumbNavigation'
import { getSitesData, assignTags, removeTags } from './thunks'
import { COMPANY_CHANGE, LOGOUT } from '../globalActions'
import { RequestState } from '../common'

export interface SitesState {
  sites: {
    [siteId: string]: Site
  }
  requestSitesState: RequestState
  breadcrumbs: BreadcrumbSegment[]
}

const initialState: SitesState = {
  sites: {},
  requestSitesState: 'idle',
  breadcrumbs: []
}

export const sitesSlice = createSlice({
  name: 'sites',
  initialState,
  reducers: {
    setTags: (state, action: PayloadAction<{ siteId: string; tags: string[] }>) => {
      const { siteId, tags } = action.payload
      state.sites[siteId].tags = tags
    },
    setSiteTagsBreadcrumbs: (state, action: PayloadAction<BreadcrumbSegment[]>) => {
      state.breadcrumbs = [...action.payload].sort((a, b) => a.order - b.order)
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getSitesData.pending, (state) => {
      state.requestSitesState = 'pending'
    })

    builder.addCase(getSitesData.fulfilled, (state, action) => {
      state.requestSitesState = 'fulfilled'
      state.sites = {} // reset
      action.payload.forEach((site) => {
        state.sites[site.id] = site
      })
    })

    builder.addCase(getSitesData.rejected, (state) => {
      state.requestSitesState = 'rejected'
      state.sites = {}
    })

    builder.addCase(assignTags.pending, () => {
      // TODO
      // The call is updating
    })

    builder.addCase(assignTags.rejected, () => {
      // TODO
      // The call failed so the backend was not updated
    })

    builder.addCase(assignTags.fulfilled, (state, action) => {
      const { siteIds, tagIds } = action.payload
      siteIds.forEach((siteId) => {
        tagIds.forEach((tagId) => {
          if (state.sites[siteId].tags.includes(tagId)) {
            return
          }
          state.sites[siteId].tags.push(tagId)
        })
      })
    })

    builder.addCase(removeTags.fulfilled, (state, action) => {
      const { siteIds, tagIds } = action.payload
      siteIds.forEach((siteId) => {
        tagIds.forEach((tagId) => {
          state.sites[siteId].tags = state.sites[siteId].tags.filter(
            (t) => t !== tagId
          )
        })
      })
    })

    // Reset state on logout
    builder.addCase(LOGOUT, () => initialState)
    builder.addCase(COMPANY_CHANGE, () => initialState)
  }
})

export const sitesReducer = sitesSlice.reducer
export const {
  setTags,
  setSiteTagsBreadcrumbs
} = sitesSlice.actions
